import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import styles from "./RequestAccept.module.css";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

/* =========================================================
   API CONFIG
========================================================= */

const API_BASE_URL =
  "https://coinyoco.world/brobizee-api";

const API_GET_SINGLE_PARTNER =
  `${API_BASE_URL}/get_single_partner.php`;

// ✅ OTP-only API
const API_GET_PARTNER_OTP =
  `${API_BASE_URL}/get_partner_otp.php`;

/* =========================================================
   DEFAULT AVATAR
========================================================= */

const DEFAULT_AVATAR =
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=300&q=80";

/* =========================================================
   ⭐ UPI APPS LIST
   ---------------------------------------------------------
   Each app has:
   - id: unique key
   - name: display name
   - scheme: deep-link scheme for that app
   - color: brand color
   - icon: emoji/letter (fallback if image fails)
   - logo: optional remote logo URL
========================================================= */

const UPI_APPS = [
  {
    id: "gpay",
    name: "Google Pay",
    scheme: "tez://upi/pay",
    color: "#4285F4",
    icon: "G",
  },
  {
    id: "phonepe",
    name: "PhonePe",
    scheme: "phonepe://pay",
    color: "#5F259F",
    icon: "प",
  },
  {
    id: "paytm",
    name: "Paytm",
    scheme: "paytmmp://pay",
    color: "#00BAF2",
    icon: "P",
  },
  {
    id: "bhim",
    name: "BHIM UPI",
    scheme: "bhim://pay",
    color: "#0066B3",
    icon: "भ",
  },
  {
    id: "amazonpay",
    name: "Amazon Pay",
    scheme: "amazonpay://pay",
    color: "#FF9900",
    icon: "a",
  },
  {
    id: "cred",
    name: "CRED",
    scheme: "credpay://pay",
    color: "#111111",
    icon: "C",
  },
  {
    id: "mobikwik",
    name: "MobiKwik",
    scheme: "mobikwik://pay",
    color: "#E52329",
    icon: "M",
  },
  {
    id: "freecharge",
    name: "Freecharge",
    scheme: "freecharge://pay",
    color: "#00C6A9",
    icon: "F",
  },
  {
    id: "slice",
    name: "Slice",
    scheme: "slice://pay",
    color: "#6C4DFF",
    icon: "S",
  },
  {
    id: "navi",
    name: "Navi",
    scheme: "navi://pay",
    color: "#FF5C5C",
    icon: "N",
  },
];

/* =========================================================
   HELPERS
========================================================= */

const safeString = (value, fallback = "") => {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value).trim() || fallback;
};

const safeNumber = (value, fallback = 0) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
};

const getProfileImage = (profile) => {
  const image = safeString(profile, "");
  if (!image) return DEFAULT_AVATAR;

  if (
    image.startsWith("http://") ||
    image.startsWith("https://") ||
    image.startsWith("data:image/")
  ) {
    return image;
  }

  if (image.startsWith("/")) {
    return `${API_BASE_URL}${image}`;
  }

  return `${API_BASE_URL}/${image}`;
};

/* =========================================================
   ⭐ UPI DEEP-LINK BUILDER
   ---------------------------------------------------------
   Builds a UPI payment URL with parameters:
   - pa : payee VPA (UPI ID)
   - pn : payee name
   - am : amount
   - cu : currency (INR)
   - tn : transaction note
========================================================= */

const buildUpiLink = ({
  scheme = "upi://pay",
  upiId,
  name,
  amount,
  note = "Payment via BroBizee",
}) => {
  const params = new URLSearchParams();

  if (upiId) params.append("pa", upiId);
  if (name) params.append("pn", name);
  if (amount && Number(amount) > 0) {
    params.append("am", String(amount));
    params.append("cu", "INR");
  }
  if (note) params.append("tn", note);

  return `${scheme}?${params.toString()}`;
};

/* =========================================================
   DISTANCE CALCULATOR — Haversine
========================================================= */

const calculateDistanceKm = (lat1, lon1, lat2, lon2) => {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;

  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c =
    2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c;
};

const formatDistance = (km) => {
  if (!Number.isFinite(km)) return "—";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(2)} km`;
};

const formatETA = (km) => {
  if (!Number.isFinite(km)) return "—";
  const hours = km / 25;
  const minutes = Math.round(hours * 60);
  if (minutes < 1) return "< 1 min";
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${m}m`;
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function RequestAccept({
  onBack,
  onCall,
  onSaveToggle,
}) {
  const navigate = useNavigate();
  const params = useParams();

  /* =========================================================
     IDENTIFIERS
  ========================================================= */

  const secondaryUuid =
    params?.secondary_uuid ||
    localStorage.getItem("selected_partner_uuid") ||
    "";

  const secondaryId =
    params?.secondary_id ||
    localStorage.getItem("selected_partner_secondary_id") ||
    "";

  const partnerId =
    params?.partnerId ||
    params?.partner_id ||
    secondaryUuid ||
    secondaryId ||
    "";

  /* STATE */
  const [isSaved, setIsSaved] = useState(true);

  const [partner, setPartner] = useState(null);
  const [loadingPartner, setLoadingPartner] = useState(true);
  const [partnerError, setPartnerError] = useState("");

  const [userLocation, setUserLocation] = useState(null);

  // ✅ OTP states
  const [otp, setOtp] = useState("");
  const [showOtp, setShowOtp] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  const [showPayment, setShowPayment] = useState(false);

  // ⭐ NEW: show more UPI apps
  const [showOtherUpi, setShowOtherUpi] = useState(false);

  /* SAVE TOGGLE */
  const handleSaveClick = useCallback(() => {
    setIsSaved((prev) => {
      const next = !prev;
      if (typeof onSaveToggle === "function") {
        onSaveToggle(next);
      }
      return next;
    });
  }, [onSaveToggle]);

  /* BACK HANDLER */
  const handleBack = useCallback(() => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }
    navigate(-1);
  }, [onBack, navigate]);

  /* HOME HANDLER — navigate to /userprofile */
  const handleGoHome = useCallback(() => {
    if (secondaryId && secondaryUuid) {
      navigate(
        `/${secondaryId}/${secondaryUuid}/userprofile`,
        { replace: true }
      );
    } else {
      const urlUserId = params?.userId || "";
      const urlUuid =
        params?.uuid || params?.deviceUuid || "";

      if (urlUserId && urlUuid) {
        navigate(
          `/${urlUserId}/${urlUuid}/userprofile`,
          { replace: true }
        );
      } else {
        navigate("/userprofile", { replace: true });
      }
    }
  }, [navigate, secondaryId, secondaryUuid, params]);

  /* CALL HANDLER */
  const handleCall = useCallback(() => {
    const phone = partner?.phone_number || "";

    if (!phone) {
      alert("Partner phone number not available.");
      return;
    }

    if (typeof onCall === "function") {
      onCall(phone);
      return;
    }

    window.location.href = `tel:${phone}`;
  }, [onCall, partner]);

  /* =========================================================
     FETCH PARTNER DATA
  ========================================================= */
  const fetchPartnerData = useCallback(async () => {
    if (!partnerId) {
      throw new Error("Partner ID missing.");
    }

    const query = new URLSearchParams();
    if (secondaryUuid) query.append("secondary_uuid", secondaryUuid);
    if (secondaryId) query.append("secondary_id", secondaryId);
    if (!secondaryUuid && !secondaryId && partnerId) {
      query.append("partner_id", partnerId);
    }

    const url = `${API_GET_SINGLE_PARTNER}?${query.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    let result;
    try {
      result = await response.json();
    } catch {
      throw new Error("Invalid JSON from server.");
    }

    if (!result?.success || !result?.partner) {
      throw new Error(result?.message || "Partner not found.");
    }

    return result.partner;
  }, [partnerId, secondaryUuid, secondaryId]);

  /* =========================================================
     FETCH OTP ONLY
  ========================================================= */
  const fetchPartnerOtp = useCallback(async () => {
    if (!secondaryUuid && !secondaryId && !partnerId) {
      throw new Error("Partner identifier missing.");
    }

    const query = new URLSearchParams();
    if (secondaryUuid) query.append("secondary_uuid", secondaryUuid);
    if (secondaryId) query.append("secondary_id", secondaryId);
    if (!secondaryUuid && !secondaryId && partnerId) {
      query.append("partner_id", partnerId);
    }

    const url = `${API_GET_PARTNER_OTP}?${query.toString()}`;

    const response = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const result = await response.json();

    if (!result?.success) {
      throw new Error(result?.message || "OTP not available.");
    }

    return {
      otp_code: safeString(result.otp_code, ""),
      otp_available: Boolean(result.otp_available),
      partner: result.partner || null,
    };
  }, [partnerId, secondaryUuid, secondaryId]);

  /* =========================================================
     INITIAL LOAD
  ========================================================= */
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoadingPartner(true);
        setPartnerError("");

        const statePartner =
          localStorage.getItem("selected_partner");

        if (statePartner) {
          try {
            const parsed = JSON.parse(statePartner);
            if (!cancelled && parsed) {
              setPartner(parsed);
            }
          } catch {
            /* ignore */
          }
        }

        const data = await fetchPartnerData();

        if (!cancelled && data) {
          setPartner(data);
        }
      } catch (err) {
        console.error("Fetch partner error:", err);

        if (!cancelled) {
          setPartnerError(
            err?.message || "Unable to load partner."
          );
        }
      } finally {
        if (!cancelled) setLoadingPartner(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [fetchPartnerData]);

  /* =========================================================
     AUTO-REFRESH PARTNER
  ========================================================= */
  useEffect(() => {
    if (!partnerId) return;

    const interval = setInterval(() => {
      fetchPartnerData()
        .then((data) => {
          if (data) setPartner(data);
        })
        .catch((err) => {
          console.warn("Auto-refresh partner failed:", err);
        });
    }, 8000);

    return () => clearInterval(interval);
  }, [partnerId, fetchPartnerData]);

  /* USER LOCATION */
  useEffect(() => {
    if (!navigator.geolocation) return;

    let watchId = null;

    try {
      watchId = navigator.geolocation.watchPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.warn("Location error:", error);
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 5000,
        }
      );
    } catch (err) {
      console.warn("Geolocation exception:", err);
    }

    return () => {
      if (watchId !== null && navigator.geolocation) {
        navigator.geolocation.clearWatch(watchId);
      }
    };
  }, []);

  /* DISTANCE */
  const partnerCoords = useMemo(() => {
    if (!partner) return null;

    const raw = safeString(partner.latitude_longitude, "");
    if (!raw) return null;

    const cleaned = raw.replace(/[()[\]]/g, "").trim();
    const parts = cleaned.split(",");
    if (parts.length < 2) return null;

    const lat = Number(parts[0].trim());
    const lng = Number(parts[1].trim());

    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      return null;
    }

    return { latitude: lat, longitude: lng };
  }, [partner]);

  const distanceKm = useMemo(() => {
    if (!userLocation || !partnerCoords) return null;

    return calculateDistanceKm(
      userLocation.latitude,
      userLocation.longitude,
      partnerCoords.latitude,
      partnerCoords.longitude
    );
  }, [userLocation, partnerCoords]);

  const distanceText = formatDistance(distanceKm);
  const etaText = formatETA(distanceKm);

  /* MAP SRC */
  const mapSrc = useMemo(() => {
    if (userLocation && partnerCoords) {
      return (
        `https://maps.google.com/maps?` +
        `saddr=${userLocation.latitude},${userLocation.longitude}` +
        `&daddr=${partnerCoords.latitude},${partnerCoords.longitude}` +
        `&z=13&output=embed`
      );
    }

    if (partnerCoords) {
      return `https://maps.google.com/maps?q=${partnerCoords.latitude},${partnerCoords.longitude}&z=15&output=embed`;
    }

    return `https://maps.google.com/maps?q=India&z=5&output=embed`;
  }, [userLocation, partnerCoords]);

  /* DERIVED DATA */
  const partnerName = safeString(partner?.full_name, "Partner");
  const partnerAvatar = getProfileImage(partner?.profile);
  const partnerWork = safeString(partner?.work, "Service Provider");
  const partnerSecondaryWork = safeString(partner?.secondary_work, "");

  const skillsText =
    [partnerSecondaryWork, partnerWork]
      .filter(Boolean)
      .join(", ") || "Professional Service";

  const partnerBio = safeString(
    partner?.bio,
    "This partner has not added a bio yet."
  );
  const partnerExperience = safeString(partner?.experiences, "—");
  const partnerAge = safeString(partner?.age, "—");
  const partnerGender = safeString(partner?.gender, "");
  const partnerTeam = safeString(partner?.helper_list, "1 Person");
  const partnerMode = safeString(partner?.mode, "2TW");
  const partnerMoney = safeNumber(partner?.money, 0);
  const partnerWorkingCharges = safeString(partner?.working_charges, "");
  const partnerAddress = safeString(partner?.address, "");
  const partnerRecentWork = safeString(partner?.recently_work, "");
  const partnerPhone = safeString(partner?.phone_number, "");
  const partnerUpi = safeString(partner?.upi_id, "");

  /* =========================================================
     OTP
  ========================================================= */
  const partnerOtp = safeString(partner?.otp_code, "");

  const handleShowOtp = useCallback(async () => {
    if (partnerOtp) {
      setOtp(partnerOtp);
      setShowOtp(true);
      return;
    }

    try {
      setOtpLoading(true);
      const result = await fetchPartnerOtp();

      if (!result.otp_available || !result.otp_code) {
        alert("OTP not available yet. Please try again in a moment.");
        return;
      }

      setOtp(result.otp_code);
      setShowOtp(true);
    } catch (err) {
      console.error("OTP fetch error:", err);
      alert(err?.message || "Unable to fetch OTP.");
    } finally {
      setOtpLoading(false);
    }
  }, [partnerOtp, fetchPartnerOtp]);

  /* =========================================================
     PAYMENT HANDLER
  ========================================================= */
  const handlePayment = useCallback(() => {
    setShowOtherUpi(false);
    setShowPayment(true);
  }, []);

  const closePayment = useCallback(() => {
    setShowPayment(false);
    setShowOtherUpi(false);
  }, []);

  /* =========================================================
     ⭐ HANDLE UPI APP CLICK — Open app via deep-link
  ========================================================= */
  const handleUpiAppClick = useCallback(
    (app) => {
      if (!partnerUpi) {
        alert(
          "Partner UPI ID is not available. Please use Cash or contact partner."
        );
        return;
      }

      const link = buildUpiLink({
        scheme: app.scheme,
        upiId: partnerUpi,
        name: partnerName,
        amount: partnerMoney,
        note: `Payment to ${partnerName} via BroBizee`,
      });

      console.log(`Opening ${app.name}:`, link);

      try {
        /* Try opening the specific app */
        window.location.href = link;

        /* Fallback: if app not installed, try generic upi:// */
        setTimeout(() => {
          if (!document.hidden) {
            const genericLink = buildUpiLink({
              scheme: "upi://pay",
              upiId: partnerUpi,
              name: partnerName,
              amount: partnerMoney,
              note: `Payment to ${partnerName} via BroBizee`,
            });
            window.location.href = genericLink;
          }
        }, 1500);
      } catch (err) {
        console.warn("UPI deep-link error:", err);

        /* Final fallback — generic UPI chooser */
        const genericLink = buildUpiLink({
          scheme: "upi://pay",
          upiId: partnerUpi,
          name: partnerName,
          amount: partnerMoney,
          note: `Payment to ${partnerName} via BroBizee`,
        });
        window.location.href = genericLink;
      }
    },
    [partnerUpi, partnerName, partnerMoney]
  );

  /* Handle "Other UPI Apps" — open system UPI chooser */
  const handleOtherUpiClick = useCallback(() => {
    if (!partnerUpi) {
      alert(
        "Partner UPI ID is not available. Please use Cash or contact partner."
      );
      return;
    }

    const link = buildUpiLink({
      scheme: "upi://pay",
      upiId: partnerUpi,
      name: partnerName,
      amount: partnerMoney,
      note: `Payment to ${partnerName} via BroBizee`,
    });

    console.log("Opening generic UPI chooser:", link);

    try {
      window.location.href = link;
    } catch (err) {
      console.warn("UPI chooser error:", err);
      alert(
        "No UPI app found on your device. Please install one to continue."
      );
    }
  }, [partnerUpi, partnerName, partnerMoney]);

  /* RENDER */
  return (
    <div className={styles.screenWrapper}>
      <div className={styles.deviceFrame}>

        {/* TOP NAVIGATION BAR */}
        <header className={styles.appBar}>
          <button
            type="button"
            className={`${styles.saveBtn} ${
              isSaved ? styles.saveActive : ""
            }`}
            onClick={handleSaveClick}
            aria-label={isSaved ? "Remove from saved" : "Save Profile"}
          >
            <span className={styles.saveIconWrap}>
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill={isSaved ? "#FF6B00" : "none"}
                stroke="#FF6B00"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
              </svg>
            </span>
            <span className={styles.saveLabel}>
              {isSaved ? "Saved" : "Save"}
            </span>
          </button>

          <h1 className={styles.headerTitle}>Profile</h1>

          <button
            type="button"
            className={styles.homeBtn}
            onClick={handleGoHome}
            aria-label="Go to User Profile"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#005CA9"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </button>
        </header>

        {/* FIXED MAP SECTION */}
        <section className={styles.mapSection}>
          <iframe
            title="Location Map"
            className={styles.mapIframe}
            src={mapSrc}
            loading="lazy"
          ></iframe>

          <div className={styles.mapOverlay}></div>

          <div className={styles.routeBar}>
            <div className={styles.routeEndpoint}>
              <span
                className={styles.routeDot}
                style={{ background: "#00c853" }}
              />
              <span className={styles.routeLabel}>You</span>
            </div>

            <div className={styles.routeLine}>
              <div className={styles.routeDash} />
              <div
                className={`${styles.routeDistance} ${
                  Number.isFinite(distanceKm)
                    ? styles.routeDistanceReady
                    : ""
                }`}
              >
                {Number.isFinite(distanceKm)
                  ? `${distanceText} • ${etaText}`
                  : "Getting location..."}
              </div>
              <div className={styles.routeDash} />
            </div>

            <div className={styles.routeEndpoint}>
              <span
                className={styles.routeDot}
                style={{ background: "#ff6b00" }}
              />
              <span className={styles.routeLabel}>Partner</span>
            </div>
          </div>

          <div className={styles.locationPill}>
            <span className={styles.locationDot}></span>
            <span>Service Location</span>
          </div>

          <div className={styles.mapZoomBadge}>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0Z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
            <span>Nearby</span>
          </div>
        </section>

        {/* SCROLLABLE PROFILE CARD */}
        <div className={styles.scrollContainer}>
          <div className={styles.profileCard}>

            <div className={styles.cardHandle}></div>

            {/* TOP ACTION ROW */}
            <div className={styles.topActionRow}>
              <button
                type="button"
                className={styles.otpBtn}
                onClick={handleShowOtp}
                disabled={otpLoading}
                title={
                  otpLoading
                    ? "Loading OTP..."
                    : "Show OTP"
                }
              >
                <span className={styles.actionIcon}>🔐</span>
                <span
                  style={{
                    fontFamily: "monospace",
                    letterSpacing: "3px",
                    fontWeight: 800,
                    fontSize: "16px",
                  }}
                >
                  {otpLoading
                    ? "Loading..."
                    : partnerOtp || "Show OTP"}
                </span>
              </button>

              <button
                type="button"
                className={styles.paymentBtn}
                onClick={handlePayment}
              >
                <span className={styles.actionIcon}>💳</span>
                <span>Pay Now</span>
              </button>
            </div>

            {/* DISTANCE / ETA SUMMARY */}
            <div className={styles.distanceSummary}>
              <div className={styles.distanceTile}>
                <span className={styles.distanceTileIcon}>📍</span>
                <div>
                  <small>Distance</small>
                  <strong>{distanceText}</strong>
                </div>
              </div>

              <div className={styles.distanceTile}>
                <span className={styles.distanceTileIcon}>⏱</span>
                <div>
                  <small>ETA</small>
                  <strong>{etaText}</strong>
                </div>
              </div>

              <div className={styles.distanceTile}>
                <span className={styles.distanceTileIcon}>🛵</span>
                <div>
                  <small>Mode</small>
                  <strong>{partnerMode}</strong>
                </div>
              </div>
            </div>

            {/* HEADER */}
            <div className={styles.cardHeader}>
              <div className={styles.avatarWrapper}>
                <div className={styles.avatarRing}>
                  <img
                    src={partnerAvatar}
                    alt={partnerName}
                    className={styles.avatarImg}
                    onError={(e) => {
                      e.currentTarget.src = DEFAULT_AVATAR;
                    }}
                  />
                </div>
                <span className={styles.onlineBadge}>
                  <span></span>
                </span>
              </div>

              <div className={styles.headerInfo}>
                <div className={styles.badgeRow}>
                  <span className={styles.assuredBadge}>
                    <span className={styles.assuredIcon}>✓</span>
                    BroBizee
                    <span className={styles.subTag}>Assured</span>
                  </span>

                  <span className={styles.categoryBadge}>
                    {partnerWork}
                  </span>

                  {partner?.account_type && (
                    <span className={styles.tierTag}>
                      {safeString(partner.account_type, "").toUpperCase()}
                    </span>
                  )}
                </div>

                <div className={styles.nameRow}>
                  <h2 className={styles.profileName}>{partnerName}</h2>
                  <div className={styles.ratingBadge}>
                    <span className={styles.ratingNum}>
                      {safeString(partner?.rating, "4.5")}
                    </span>
                    <span className={styles.starIcon}>★</span>
                  </div>
                </div>

                <div className={styles.metaRow}>
                  <span className={styles.metaText}>
                    <span className={styles.metaIcon}>👥</span>
                    {partnerTeam}
                  </span>

                  <span className={styles.viewsCount}>
                    <span>Views</span>
                    <strong>{safeString(partner?.views, "0")}</strong>
                  </span>
                </div>

                <div className={styles.metaSubRow}>
                  <span className={styles.metaText}>
                    <strong>Exp.</strong> {partnerExperience}
                  </span>

                  <span className={styles.metaText}>
                    <span className={styles.metaIcon}>👤</span>
                    {partnerAge}
                    {partnerGender ? " • " : ""}
                    {partnerGender}
                  </span>
                </div>
              </div>
            </div>

            {/* QUICK STATS */}
            <div className={styles.quickStats}>
              <div className={styles.statItem}>
                <span className={styles.statIcon}>₹</span>
                <div>
                  <small>Starting From</small>
                  <strong>
                    {partnerMoney ? `${partnerMoney} Rs` : "Contact"}
                  </strong>
                </div>
              </div>

              <div className={styles.statDivider}></div>

              <div className={styles.statItem}>
                <span className={styles.statIcon}>🚲</span>
                <div>
                  <small>Mode</small>
                  <strong>{partnerMode}</strong>
                </div>
              </div>

              <div className={styles.statDivider}></div>

              <div className={styles.statItem}>
                <span className={styles.statIcon}>👍</span>
                <div>
                  <small>Likes</small>
                  <strong>{safeString(partner?.likes, "0")}</strong>
                </div>
              </div>
            </div>

            {/* SKILLS */}
            <div className={styles.infoCard}>
              <div className={styles.infoTitleRow}>
                <div className={styles.infoTitleIcon}>
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.2-3.2a6 6 0 0 1-7.6 7.6l-6.6 6.6a2.12 2.12 0 0 1-3-3l6.6-6.6a6 6 0 0 1 7.6-7.6l-3.2 3.2Z" />
                  </svg>
                </div>
                <span>Skills</span>
              </div>
              <p className={styles.valueText}>{skillsText}</p>
            </div>

            {/* RECENT WORK */}
            {partnerRecentWork && (
              <div className={styles.workCard}>
                <div className={styles.workTopRow}>
                  <div className={styles.infoTitleRow}>
                    <div className={styles.infoTitleIcon}>
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect
                          width="20"
                          height="14"
                          x="2"
                          y="7"
                          rx="2"
                          ry="2"
                        />
                        <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                      </svg>
                    </div>
                    <span>Recently Worked At</span>
                  </div>

                  <span className={styles.distanceTag}>
                    {Number.isFinite(distanceKm)
                      ? `~ ${distanceText}`
                      : "—"}
                  </span>
                </div>

                <p className={styles.workCompany}>
                  {partnerRecentWork}
                </p>
              </div>
            )}

            {/* ADDRESS */}
            {partnerAddress && (
              <div className={styles.infoCard}>
                <div className={styles.infoTitleRow}>
                  <div className={styles.infoTitleIcon}>
                    <svg
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M21 10c0 7-9 12-9 12S3 17 3 10a9 9 0 1 1 18 0Z" />
                      <circle cx="12" cy="10" r="3" />
                    </svg>
                  </div>
                  <span>Address</span>
                </div>
                <p className={styles.valueText}>{partnerAddress}</p>
              </div>
            )}

            {/* BIO */}
            <div className={styles.bioSection}>
              <div className={styles.bioHeaderRow}>
                <div>
                  <span className={styles.sectionEyebrow}>
                    ABOUT PROFESSIONAL
                  </span>
                  <h3 className={styles.sectionTitle}>Bio</h3>
                </div>

                <button
                  type="button"
                  className={styles.callNowBtn}
                  onClick={handleCall}
                  disabled={!partnerPhone}
                >
                  <span className={styles.callIcon}>
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 1 2.81.7A2 2 0 0 1 22 16.92z" />
                    </svg>
                  </span>
                  <span>{partnerPhone ? "Call Now" : "No Number"}</span>
                </button>
              </div>

              <p className={styles.bioContent}>{partnerBio}</p>
            </div>

            {/* CHARGES */}
            <div className={styles.infoCard}>
              <div className={styles.infoTitleRow}>
                <div className={styles.infoTitleIcon}>₹</div>
                <span>Charges</span>
              </div>
              <p className={styles.valueText}>
                Money: ₹ {partnerMoney || "—"}
                {"  •  "}
                Working Charges: {partnerWorkingCharges || "—"}
              </p>
            </div>

          </div>
        </div>
      </div>

      {/* OTP POPUP */}
      {showOtp && (
        <div
          className={styles.popupOverlay}
          onClick={() => setShowOtp(false)}
        >
          <div
            className={styles.popupCard}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.popupTopStrip} />

            <div className={styles.popupIconWrap}>🔐</div>

            <span className={styles.popupEyebrow}>SERVICE OTP</span>

            <h3 className={styles.popupTitle}>
              Share this OTP with Partner
            </h3>

            <p className={styles.popupMessage}>
              Partner will use this OTP to start your service.
              Do not share with anyone else.
            </p>

            <div className={styles.otpCodeBox}>
              {(otp || "----").split("").map((digit, index) => (
                <span key={index} className={styles.otpDigit}>
                  {digit}
                </span>
              ))}
            </div>

            <button
              type="button"
              className={styles.popupPrimaryBtn}
              onClick={() => setShowOtp(false)}
            >
              Got It
            </button>
          </div>
        </div>
      )}

      {/* =========================================================
          ⭐ PAYMENT POPUP WITH UPI APPS (SCROLLABLE)
      ========================================================= */}
      {showPayment && (
        <div
          className={styles.popupOverlay}
          onClick={closePayment}
        >
          <div
            className={styles.popupCard}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxHeight: "85vh",
              overflowY: "auto",
              overflowX: "hidden",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "thin",
            }}
          >
            <div className={styles.popupTopStrip} />

            <div className={styles.popupIconWrap}>💳</div>

            <span className={styles.popupEyebrow}>
              PAYMENT
            </span>

            <h3 className={styles.popupTitle}>
              Pay {partnerName}
            </h3>

            <p className={styles.popupMessage}>
              Choose a UPI app to pay ₹ {partnerMoney || 0}{" "}
              to {partnerName}.
            </p>

            {/* AMOUNT BOX */}
            <div
              className={styles.paymentAmount}
              style={{ marginBottom: "14px" }}
            >
              <span>Amount</span>
              <strong>₹ {partnerMoney || "—"}</strong>
            </div>

            {/* UPI ID INFO */}
            {partnerUpi ? (
              <div
                style={{
                  background: "#f3f6ff",
                  border: "1px dashed #c7d2fe",
                  borderRadius: "12px",
                  padding: "10px 12px",
                  marginBottom: "14px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  fontSize: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "10px",
                      color: "#6b7280",
                      fontWeight: 600,
                      letterSpacing: "0.5px",
                    }}
                  >
                    PAYING TO UPI ID
                  </div>
                  <div
                    style={{
                      fontWeight: 800,
                      color: "#005CA9",
                      fontSize: "13px",
                      marginTop: "2px",
                      fontFamily: "monospace",
                    }}
                  >
                    {partnerUpi}
                  </div>
                </div>
                <span style={{ fontSize: "18px" }}>🏦</span>
              </div>
            ) : (
              <div
                style={{
                  background: "#fff4e5",
                  border: "1px solid #ffd8a8",
                  borderRadius: "12px",
                  padding: "10px 12px",
                  marginBottom: "14px",
                  fontSize: "12px",
                  color: "#92400e",
                  fontWeight: 600,
                }}
              >
                ⚠️ Partner UPI ID not available. Please pay via Cash.
              </div>
            )}

            {/* UPI APPS GRID — scrollable */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns:
                  "repeat(3, 1fr)",
                gap: "10px",
                marginBottom: "14px",
              }}
            >
              {UPI_APPS.map((app) => (
                <button
                  key={app.id}
                  type="button"
                  onClick={() =>
                    handleUpiAppClick(app)
                  }
                  disabled={!partnerUpi}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    padding: "12px 6px",
                    background: "#ffffff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "14px",
                    cursor: partnerUpi
                      ? "pointer"
                      : "not-allowed",
                    opacity: partnerUpi ? 1 : 0.5,
                    transition:
                      "transform 0.15s ease, box-shadow 0.15s ease",
                    boxShadow:
                      "0 1px 3px rgba(0,0,0,0.04)",
                  }}
                  onMouseDown={(e) => {
                    e.currentTarget.style.transform =
                      "scale(0.95)";
                  }}
                  onMouseUp={(e) => {
                    e.currentTarget.style.transform =
                      "scale(1)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform =
                      "scale(1)";
                  }}
                >
                  <div
                    style={{
                      width: "38px",
                      height: "38px",
                      borderRadius: "50%",
                      background: app.color,
                      color: "#ffffff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "18px",
                      fontWeight: 800,
                      boxShadow: `0 4px 10px ${app.color}40`,
                    }}
                  >
                    {app.icon}
                  </div>
                  <span
                    style={{
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#111827",
                      textAlign: "center",
                      lineHeight: 1.2,
                    }}
                  >
                    {app.name}
                  </span>
                </button>
              ))}
            </div>

            {/* OTHER UPI APPS BUTTON */}
            <button
              type="button"
              onClick={handleOtherUpiClick}
              disabled={!partnerUpi}
              style={{
                width: "100%",
                padding: "12px 14px",
                background: "#f3f6ff",
                border: "1px dashed #93b4ff",
                borderRadius: "12px",
                color: "#005CA9",
                fontSize: "13px",
                fontWeight: 800,
                cursor: partnerUpi
                  ? "pointer"
                  : "not-allowed",
                opacity: partnerUpi ? 1 : 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
                marginBottom: "14px",
              }}
            >
              <span style={{ fontSize: "16px" }}>📱</span>
              <span>Other UPI Apps</span>
            </button>

            {/* CASH / CASH ON SERVICE NOTE */}
            <div
              style={{
                background: "#f9fafb",
                borderRadius: "12px",
                padding: "10px 12px",
                marginBottom: "14px",
                fontSize: "11px",
                color: "#6b7280",
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              💵 You can also pay via Cash directly to
              the partner after service completion.
            </div>

            {/* CLOSE BUTTON */}
            <button
              type="button"
              className={styles.popupPrimaryBtn}
              onClick={closePayment}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}